import * as React from 'react';

import {CustomizeFieldsFooter} from './customize-fields-footer';
import {CustomizeModalTabs} from './customize-modal-tabs';
import {OrderFields} from './order-fields';
import {SelectFields} from './select-fields';
import type {EntityFormType, ModalHeaderContentType, SelectableField} from './types';

import styles from './customize-fields.module.css';

type Props = {
    onClose: () => void;
    entityType: EntityFormType;
    handleSaveFields: (selectedFields: SelectableField[]) => void;
    selectedFields: SelectableField[];
    nutshellFields: any[];
    customFields: any[];
    isSaving: boolean;
    schemaFields: any[];
    limit?: number;
    canRequireFields?: boolean;
    isLeadCard?: boolean;
};

export function CustomizeFields(props: Props) {
    const [selectedTab, setSelectedTab] = React.useState<ModalHeaderContentType>('SELECT');
    const [selectedFieldOrder, setSelectedFieldOrder] = React.useState<SelectableField[]>(
        props.selectedFields.map((field) => ({
            __typename: 'SelectableField',
            fieldId: field.fieldId,
            isRequired: field.isRequired,
            isRemoveable: field.isRemoveable,
        }))
    );

    const handleCheckboxToggle = (fieldKey: string) => {
        setSelectedFieldOrder((prevState) => {
            if (prevState.find((field) => field.fieldId === fieldKey)) {
                return prevState.filter((field) => field.fieldId !== fieldKey);
            } else {
                const selectedField: SelectableField = {
                    __typename: 'SelectableField',
                    fieldId: fieldKey,
                    isRequired: false,
                    isRemoveable: true,
                };

                // We want to insert the aiSummary field as close to the top of
                // the list as possible, right below any non-removeable fields
                if (fieldKey === 'aiSummary') {
                    const findIndex = prevState.findIndex((field) => field.isRemoveable);
                    const insertIndex = findIndex === -1 ? prevState.length : findIndex;

                    const nextState = [...prevState];
                    nextState.splice(insertIndex, 0, selectedField);

                    return nextState;
                }

                return prevState.concat(selectedField);
            }
        });
    };

    return (
        <>
            <div className={styles.container}>
                <CustomizeModalTabs
                    selectedTab={selectedTab}
                    onChange={(tab: ModalHeaderContentType) => {
                        setSelectedTab(tab);
                    }}
                    selectedCount={selectedFieldOrder.length}
                    limit={props.limit}
                />
                {selectedTab === 'SELECT' ? (
                    <SelectFields
                        nutshellFields={props.nutshellFields}
                        customFields={props.customFields}
                        selectedFields={selectedFieldOrder}
                        // @ts-expect-error
                        setSelectedFields={setSelectedFieldOrder}
                        entityType={props.entityType}
                        handleCheckboxToggle={handleCheckboxToggle}
                        isLeadCard={props.isLeadCard}
                    />
                ) : (
                    <OrderFields
                        schemaFields={props.schemaFields}
                        selectedFields={selectedFieldOrder}
                        entityType={props.entityType}
                        canRequireFields={props.canRequireFields}
                        isLeadCard={props.isLeadCard}
                        onReorderFields={(updatedFields) => {
                            setSelectedFieldOrder(
                                updatedFields.map((fieldId) => {
                                    const selectedField = selectedFieldOrder.find(
                                        (field) => field.fieldId === fieldId
                                    );

                                    return {
                                        __typename: 'SelectableField',
                                        fieldId,
                                        isRequired: selectedField
                                            ? selectedField.isRequired
                                            : false,
                                        isRemoveable: selectedField
                                            ? selectedField.isRemoveable
                                            : true,
                                    };
                                })
                            );
                        }}
                        onSetRequired={(updatedField: SelectableField) => {
                            setSelectedFieldOrder(
                                selectedFieldOrder.map((selectedField) => {
                                    return selectedField.fieldId === updatedField.fieldId
                                        ? updatedField
                                        : selectedField;
                                })
                            );
                        }}
                    />
                )}
            </div>
            <CustomizeFieldsFooter
                isSaving={props.isSaving}
                handleSaveFields={() => props.handleSaveFields(selectedFieldOrder)}
                onClose={props.onClose}
                errorMessage={
                    props.limit && selectedFieldOrder.length > props.limit
                        ? `Reduce your selection to a maximum of ${props.limit} fields to continue`
                        : undefined
                }
            />
        </>
    );
}
